import { FeatureCalendar } from '@mdlbeasts/nofomo.ui.web';
import { toZonedTime } from 'date-fns-tz';
import { IntlShape } from 'react-intl';

import { DateFormat, i18nFormat } from 'utils/date.utils';

import { SiteLocale } from 'constants/i18n.constants';
import { EventCardFragment } from 'types/api/dato-operations.types';

// One image serves both the small day cover and the day-sheet row.
const IMAGE_SIZE = 160;

const toImageUrl = (url: string) =>
  `${url}?w=${IMAGE_SIZE}&h=${IMAGE_SIZE}&fit=crop&auto=format`;

interface Props {
  events: Array<EventCardFragment> | undefined;
  intl: IntlShape;
}

// Dato events as the kit's calendar events, each set in its own timezone (how
// the kit groups them). Events with no date are skipped.
export const toCalendarEvents = ({
  events,
  intl,
}: Props): Array<FeatureCalendar.CalendarEvent> =>
  (events ?? []).flatMap((event) => {
    if (!event.startDatetime || !event.endDatetime) return [];

    const city = event.venue?.city?.name;

    return [
      {
        id: event.slug,
        start: toZonedTime(new Date(event.startDatetime), event.timezone),
        end: toZonedTime(new Date(event.endDatetime), event.timezone),
        name: event.title ?? '',
        image: event.cover?.url ? toImageUrl(event.cover.url) : '',
        location: city
          ? intl.formatMessage({ id: 'event.city.in' }, { city })
          : '',
      },
    ];
  });

// SUN-SAT headings for the current locale, as the design shows them.
export const toWeekdayLabels = (locale: SiteLocale): Array<string> =>
  Array.from({ length: 7 }, (_, index) =>
    i18nFormat(
      // Jan 7 2024 is a Sunday, so the row reads SUN-SAT whatever the locale.
      new Date(2024, 0, 7 + index),
      DateFormat.WeekdayShort,
      locale,
    ).toUpperCase(),
  );
